import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import UserPackagesManagementApi from '@src/apis/userPackagesManagement';
import { COLOR } from '@src/styles/color';

const EditUserCreditsModal = ({ open, userPackage, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [credits, setCredits] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userPackage) {
      setCredits(userPackage.remainingCredits?.toString() || '');
    }
  }, [userPackage]);

  const handleClose = () => {
    setCredits('');
    setLoading(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!credits || Number.isNaN(credits) || parseInt(credits, 10) < 0) {
      enqueueSnackbar(t('pleaseEnterValidCredits'), { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      const response = await UserPackagesManagementApi.updateUserCredits(
        userPackage.userId.id,
        { credits: parseInt(credits, 10) },
      );

      if (response?.status) {
        enqueueSnackbar(t('updateUserCreditsSuccess'), {
          variant: 'success',
        });
        onSuccess();
      }
    } catch (error) {
      enqueueSnackbar(t('updateUserCreditsError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreditsChange = ({ target: { value } }) => {
    if (value === '' || /^\d+$/.test(value)) {
      setCredits(value);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${COLOR.charcoal[16]}`,
          padding: '24px 24px 16px 24px',
        }}
      >
        <Typography variant="h5" sx={{ color: COLOR.text.primary }}>
          {t('editUserCredits')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ padding: '24px' }}>
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="body2" sx={{ marginBottom: '8px' }}>
            {t('userName')}:{' '}
            <strong>{userPackage?.userId?.name || '--'}</strong>
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: '16px' }}>
            {t('currentCredits')}:{' '}
            <strong>{userPackage?.remainingCredits || 0}</strong>
          </Typography>
        </Box>

        <TextField
          fullWidth
          label={t('newCredits')}
          value={credits}
          onChange={handleCreditsChange}
          type="text"
          placeholder={t('enterCredits')}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: COLOR.white[100],
              borderRadius: '8px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: COLOR.primary.light,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: COLOR.primary.main,
              },
            },
          }}
          helperText={t('creditsMustBePositiveNumber')}
        />
      </DialogContent>

      <DialogActions
        sx={{
          padding: '16px 24px 24px 24px',
          borderTop: `1px solid ${COLOR.charcoal[16]}`,
          gap: '12px',
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            color: COLOR.text.secondary,
            borderColor: COLOR.charcoal[32],
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              borderColor: COLOR.charcoal[48],
              backgroundColor: COLOR.charcoal[8],
            },
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !credits}
          sx={{
            background: `linear-gradient(135deg, ${COLOR.primary.main} 0%, ${COLOR.primary.dark} 100%)`,
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 24px',
            '&:hover': {
              background: `linear-gradient(135deg, ${COLOR.primary.dark} 0%, ${COLOR.primary.main} 100%)`,
            },
            '&:disabled': {
              background: COLOR.charcoal[32],
              color: COLOR.text.disabled,
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CircularProgress size={16} color="inherit" />
              {t('updating')}
            </Box>
          ) : (
            t('update')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserCreditsModal;
